import React, { useState } from 'react';
import { Form, Button, Alert, InputGroup } from 'react-bootstrap';
import { Link, useNavigate, useSearchParams } from 'react-router-dom';
import { useAuth } from '../contexts/AuthContext';
import { toast } from 'react-toastify';
import PublicPageLayout from './PublicPageLayout';
import PasswordHint from './PasswordHint';
import { passwordProblem } from '../utils/passwordPolicy';

function ResetPassword() {
  const [searchParams] = useSearchParams();
  const token = searchParams.get('token') || '';
  // A new user choosing their first password, from the link their administrator's invitation sent
  const welcome = searchParams.get('welcome') === '1';

  const [formData, setFormData] = useState({
    password: '',
    confirmPassword: ''
  });
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState('');
  const [showPassword, setShowPassword] = useState(false);

  const { resetPassword } = useAuth();
  const navigate = useNavigate();

  const handleChange = (e) => {
    setFormData({
      ...formData,
      [e.target.name]: e.target.value
    });
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    setError('');

    if (!token) {
      setError('This reset link is missing or invalid.');
      return;
    }
    const weak = passwordProblem(formData.password);
    if (weak) {
      setError(weak);
      return;
    }
    if (formData.password !== formData.confirmPassword) {
      setError('Passwords do not match');
      return;
    }

    setLoading(true);
    const result = await resetPassword(token, formData.password);

    if (result.success) {
      toast.success(welcome ? 'Password set. Sign in with your email address and new password.' : (result.message || 'Password reset successfully'));
      navigate('/login');
    } else {
      setError(result.error);
      toast.error('Unable to reset password');
    }

    setLoading(false);
  };

  return (
    <PublicPageLayout subtitle={welcome ? 'Welcome! Choose your password' : 'Choose a new password'}>
      {error && <Alert variant="danger">{error}</Alert>}

      {!token ? (
        <Alert variant="warning">
          This reset link is missing or invalid.{' '}
          <Link to="/forgot-password">Request a new link</Link>
        </Alert>
      ) : (
        <Form onSubmit={handleSubmit}>
          <Form.Group className="mb-3">
            <Form.Label>{welcome ? 'Your password' : 'New password'}</Form.Label>
            <InputGroup>
              <Form.Control
                type={showPassword ? 'text' : 'password'}
                name="password"
                value={formData.password}
                onChange={handleChange}
                required
                autoComplete="new-password"
                placeholder="Choose a strong password"
              />
              <Button
                variant="outline-secondary"
                onClick={() => setShowPassword(!showPassword)}
                style={{ borderLeft: 'none' }}
                aria-label={showPassword ? 'Hide password' : 'Show password'}
              >
                {showPassword ? 'Hide' : 'Show'}
              </Button>
            </InputGroup>
            <PasswordHint password={formData.password} />
          </Form.Group>

          <Form.Group className="mb-3">
            <Form.Label>Confirm password</Form.Label>
            <Form.Control
              type={showPassword ? 'text' : 'password'}
              name="confirmPassword"
              value={formData.confirmPassword}
              onChange={handleChange}
              required
              minLength={6}
              placeholder="Re-enter new password"
            />
          </Form.Group>

          <Button
            variant="primary"
            type="submit"
            className="w-100"
            disabled={loading}
          >
            {loading ? 'Updating...' : 'Update password'}
          </Button>

          <div className="text-center mt-3">
            <Link to="/login">Back to sign in</Link>
          </div>
        </Form>
      )}
    </PublicPageLayout>
  );
}

export default ResetPassword;
